import { useTranslation } from 'react-i18next';
import { classifyQueryError } from './queryError';

interface QueryErrorStateProps {
  error: unknown;
  onRetry: () => void;
  /**
   * The screen's OWN generic-error copy, already translated. Used for the
   * `transient` and `permanent` kinds so each screen keeps the wording it had —
   * the slice does not silently reword nine screens. The `suspended` kind
   * overrides these with the shared `errors.suspended.*` copy, because a
   * suspended tenant is the same state on every screen.
   */
  title: string;
  body: string;
  retryLabel: string;
  /**
   * `panel` — the tall card with the error icon (Vehicles, Dashboard).
   * `card` — the compact card, no icon (Alerts, Trips, Zones, detail tabs).
   * Callers with a bespoke frame (the live-map overlay, the vehicle-detail
   * back-button panel) use `classifyQueryError` directly instead.
   */
  variant?: 'panel' | 'card';
}

function ErrorIcon() {
  return (
    <span
      className="mx-auto flex size-16 items-center justify-center rounded-full bg-raad-error/12"
      aria-hidden="true"
    >
      <svg
        width="30"
        height="30"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        className="text-raad-error"
      >
        <circle cx="12" cy="12" r="9" />
        <path d="M12 8v4 M12 16h.01" />
      </svg>
    </span>
  );
}

/**
 * One shared query-error state, adopted by the nine screens that hand-rolled
 * their own icon + title + body + "Try again". The value it centralises is the
 * DECISION — retry-or-not, and the suspended-tenant branch — not just the markup.
 */
export function QueryErrorState({
  error,
  onRetry,
  title,
  body,
  retryLabel,
  variant = 'card',
}: QueryErrorStateProps) {
  const { t } = useTranslation();
  const kind = classifyQueryError(error);
  const suspended = kind === 'suspended';
  const showRetry = kind === 'transient';

  const heading = suspended ? t('errors.suspended.title') : title;
  const message = suspended ? t('errors.suspended.body') : body;

  if (variant === 'panel') {
    return (
      <section className="rounded-panel border border-raad-border bg-raad-panel px-6 py-12 text-center sm:py-16">
        <ErrorIcon />
        <h2 className="mt-5 font-display text-lg font-bold">{heading}</h2>
        <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-raad-muted">{message}</p>
        {showRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="mt-5 min-h-11 rounded-xl bg-raad-primary px-5 text-sm font-semibold text-raad-onprimary transition-colors hover:bg-raad-accent"
          >
            {retryLabel}
          </button>
        ) : null}
      </section>
    );
  }

  return (
    <section className="rounded-panel border border-raad-border bg-raad-panel px-6 py-10 text-center">
      <h3 className="font-display text-base font-bold text-raad-text">{heading}</h3>
      <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-raad-muted">{message}</p>
      {showRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="mt-5 min-h-11 rounded-xl bg-raad-primary px-5 text-sm font-semibold text-raad-onprimary transition-colors hover:bg-raad-accent"
        >
          {retryLabel}
        </button>
      ) : null}
    </section>
  );
}
